'use client';

/**
 * DataGrid — Phase A1 universal list surface.
 *
 * Composes the existing `DataTable` primitive with a standard toolbar
 * (title + count + search + filter chips + actions + CSV) and
 * configurable empty/error/loading slots. Every list page in the
 * platform should land on this component over the next few sprints
 * (Phase B starts the migration; this is the contract).
 *
 * Spec: `UX_PRODUCTION_ROADMAP.md` §A1.
 *
 * Codex Phase A pass 1 (2026-05-27) refinements:
 * - `enableCsvExport` is now `onExportRequest` — a consumer callback.
 *   This keeps PHI-aware redaction + audit-logging on the existing
 *   `apps/web/src/lib/csv-export.ts` path; the grid renders the
 *   button but never touches data directly. `buildCsv` / `downloadCsv`
 *   helpers are still exported for non-PHI / dev tooling.
 * - Bulk actions pass through to `DataTable` (no wrapper BulkActionBar
 *   — that caused a duplicate "N selected" bar).
 * - Dropped `defaultSort`, single-select mode, `disabled` bulk actions
 *   from the public API — they weren't reaching DataTable. Better a
 *   smaller stable contract than props that silently ignore inputs.
 *
 * Owned-by-grid:
 * - Loading skeleton (matches row count — rows × skeleton bars)
 * - Empty state (configurable copy + optional CTA)
 * - Error state (configurable message + retry callback)
 * - Toolbar composition (search debounce, filter chips, CSV button)
 *
 * Owned-by-consumer:
 * - Data fetching (Supabase / API)
 * - Filter pipeline — toolbar emits callbacks; consumer mutates query
 *   state and re-renders with new `data`
 * - CSV export — consumer wires `onExportRequest` to an audited helper
 * - Pagination — pass-through to DataTable's existing `pageSize`
 */

import React from 'react';
import { AlertCircle, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  DataTable,
  type DataTableColumn,
  type BulkAction,
  type DataTableMobileCardBreakpoint,
  type SortState,
  type DataTableServerPagination,
} from '../data-table';
import { EmptyState } from '../empty-state';
import { ErrorState } from '../error-state';
import { LoadingSkeleton } from '../loading-skeleton';
import { DataGridToolbar, type DataGridToolbarProps } from './data-grid-toolbar';
import {
  DataGridRegisterToolbar,
  type DataGridRegisterToolbarProps,
} from './data-grid-register-toolbar';
import { DataGridFilterControls } from './data-grid-filter-controls';
import { DataGridColumnControls, type DataGridDensity } from './data-grid-column-controls';
import type {
  ActiveFilters,
  DataGridColumn,
  DataGridEmptyState,
  DataGridSelection,
  FilterSpec,
} from './types';

export type DataGridToolbarMode = 'integrated' | 'external' | 'register';
export type DataGridServerPagination = DataTableServerPagination;

export interface DataGridProps<T> {
  /**
   * The data to render. Empty array + `!loading` + `!error` shows the
   * empty state.
   *
   * In client mode this is the full dataset. With `pagination`, it is
   * exactly the current server page; the grid never slices it again.
   */
  data: readonly T[];
  /** Loading flag — drives the skeleton. */
  loading?: boolean;
  /** Error to surface instead of data. Pass `null` / undefined when no error. */
  error?: Error | null;
  /** Optional retry callback rendered on the error state. */
  onRetry?: () => void;

  /** Column specs. See `DataGridColumn`. */
  columns: ReadonlyArray<DataGridColumn<T>>;

  /** Filter dimensions — rendered as chips in the toolbar. */
  filters?: ReadonlyArray<FilterSpec>;
  /** Active filter values keyed by `FilterSpec.id`. */
  activeFilters?: ActiveFilters;
  /** Consumer wires this to clear a single filter. */
  onFilterClear?: (filterId: string) => void;
  /** Consumer wires this to clear ALL filters. */
  onClearAllFilters?: () => void;

  /** Title shown in the toolbar. Omit for a title-less grid. */
  title?: string;
  /** Omit the integrated toolbar when a separate register toolbar is rendered. */
  toolbarMode?: DataGridToolbarMode;
  /** Register composition overrides; controls still come from the grid's existing props. */
  registerToolbar?: DataGridRegisterToolbarProps['registerToolbar'];

  /** Debounced search wiring. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  /** Right-side action slot (e.g. "+ New", "Refresh"). */
  actions?: React.ReactNode;
  /** Roster opt-in: one toolbar, secondary controls in More. */
  compactControls?: boolean;
  moreControls?: React.ReactNode;
  primaryControls?: React.ReactNode;

  /**
   * CSV export callback. When supplied, the toolbar renders a CSV button
   * that calls this handler. The consumer is responsible for:
   * 1. running the export through `apps/web/src/lib/csv-export.ts` (or
   *    equivalent) so PHI redaction + audit logging fire
   * 2. providing the column visibility map to `buildCsv` if needed
   *
   * The grid intentionally does NOT export data directly — that would
   * bypass the platform's PHI controls.
   */
  onExportRequest?: () => void;

  /** Selection wiring. Omit for non-selectable grids. */
  selection?: DataGridSelection<T>;
  /** Controlled selected-row keys (set of `selection.rowKey(row)`). */
  selectedRows?: Set<string>;
  /** Called when selection changes. */
  onSelectionChange?: (selected: Set<string>) => void;

  /** Compatibility selection props; prefer `selection` for new grids. */
  selectable?: boolean;
  isRowSelectable?: (row: T) => boolean;
  rowKey?: (row: T) => string;
  bulkActions?: BulkAction[];
  /** Compatibility toolbar slot while existing lists adopt DataGridToolbar inputs. */
  toolbar?: React.ReactNode;

  /**
   * Governed empty-state config. During the bounded DataTable migration, omitting this prop selects
   * the legacy-compatible rendering path (10-row pagination, table-row empty copy, no landmark).
   */
  emptyState?: DataGridEmptyState;
  /** Compatibility copy for existing DataTable consumers moving onto DataGrid. */
  emptyMessage?: string;

  /** Row-click navigation. Index is the current sorted/paginated table index. */
  onRowClick?: (row: T, index: number) => void;

  /** Optional compact card renderer used below the configured responsive breakpoint. */
  mobileCardRenderer?: (row: T, index: number) => React.ReactNode;
  /** Breakpoint where the full table replaces compact cards. Defaults to lg. */
  mobileCardBreakpoint?: DataTableMobileCardBreakpoint;

  /** Per-row class override (e.g. status-based row highlighting). Forwarded to DataTable. */
  rowClassName?: string | ((row: T, index: number) => string);

  /** Optional per-row `data-testid` (e.g. for E2E row targeting). Forwarded to DataTable. */
  rowTestId?: string | ((row: T, index: number) => string);

  /** Column visibility map (key → visible). Forwarded to DataTable. */
  columnVisibility?: Record<string, boolean>;

  /** Page size for the underlying DataTable. Defaults to 25, or 10 in legacy compatibility mode. */
  pageSize?: number;
  /** Caller-owned server paging; cursor stacks remain with the caller. */
  pagination?: DataGridServerPagination;

  /** Sticky header on long tables. Default false (DataTable's default). */
  stickyHeader?: boolean;

  // ── Phase F1 Sprint 1 — filter setters + column controls + density ──

  /**
   * Fired when ANY individual filter dimension changes via the in-toolbar
   * setter widgets. When omitted, the toolbar still shows filter chips
   * (read-only display + clear) but no setter row.
   */
  onFilterChange?: (filterId: string, value: unknown) => void;

  /**
   * User-ordered list of column keys. Drives both the column order
   * rendered in the table and the order shown in the Customise panel.
   * When omitted, columns render in declaration order.
   */
  columnOrder?: ReadonlyArray<string>;

  /** Fired when the user reorders columns via the Customise panel. */
  onColumnOrderChange?: (nextOrder: ReadonlyArray<string>) => void;

  /** Fired when the user toggles a column's visibility. */
  onColumnVisibilityChange?: (key: string, visible: boolean) => void;

  /** Current density. Defaults to 'comfortable'. */
  density?: DataGridDensity;

  /** Fired when the user picks a new density via the Customise panel. */
  onDensityChange?: (density: DataGridDensity) => void;

  /**
   * Hint that the user has diverged from the default column / density
   * preferences. Toggles the "modified" dot on the Customise button.
   */
  isCustomised?: boolean;

  /**
   * Optional reset handler. When supplied the Customise panel renders a
   * "Reset" affordance that triggers it.
   */
  onResetCustomisation?: () => void;

  // ── Phase F1 Sprint 2 — server-side sort + page-size selector ──

  /**
   * Controlled sort state. When supplied alongside `onSortChange`, the
   * underlying DataTable switches to controlled mode — it emits sort
   * changes but does NOT re-sort the data (consumer fetches sorted data
   * server-side). Pass `{ key: null, direction: null }` for "no active
   * sort".
   */
  /** Controlled client state for URL-backed registers. Omit to retain internal paging. */
  clientPage?: number;
  onClientPageChange?: (page: number) => void;
  /** Controlled sort defaults to caller/server sorting; client opts into local sorting. */
  sortMode?: 'client' | 'server';
  sort?: SortState;
  onSortChange?: (next: SortState) => void;

  /**
   * Page-size selector options rendered in the table footer. When
   * supplied alongside `onPageSizeChange`, the user can pick a new
   * page size; the grid persists the choice via the callback.
   */
  pageSizeOptions?: ReadonlyArray<number>;
  itemLabel?: { singular: string; plural: string };
  onPageSizeChange?: (size: number) => void;

  // ── Phase F1 Sprint 2 — saved views ──

  /**
   * Saved-views slot rendered inside the toolbar action row. Typically
   * the `<DataGridSavedViews />` widget bound to `useDataGridViews`.
   */
  savedViewsSlot?: React.ReactNode;

  className?: string;
}

export function DataGrid<T extends Record<string, unknown>>({
  data,
  loading,
  error,
  onRetry,
  columns,
  filters,
  activeFilters,
  onFilterClear,
  onClearAllFilters,
  title,
  toolbarMode = 'integrated',
  registerToolbar,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  actions,
  compactControls = false,
  moreControls,
  primaryControls,
  onExportRequest,
  selection,
  selectedRows,
  onSelectionChange,
  selectable,
  isRowSelectable,
  rowKey: compatibilityRowKey,
  bulkActions: compatibilityBulkActions,
  toolbar,
  emptyState,
  emptyMessage,
  onRowClick,
  mobileCardRenderer,
  mobileCardBreakpoint,
  rowClassName,
  rowTestId,
  columnVisibility,
  pageSize,
  pagination,
  stickyHeader,
  onFilterChange,
  columnOrder,
  onColumnOrderChange,
  onColumnVisibilityChange,
  density = 'comfortable',
  onDensityChange,
  isCustomised,
  onResetCustomisation,
  sort,
  onSortChange,
  clientPage,
  onClientPageChange,
  sortMode = 'server',
  pageSizeOptions,
  itemLabel,
  onPageSizeChange,
  savedViewsSlot,
  className,
}: DataGridProps<T>) {
  const legacyDataTableCompatibility = emptyState === undefined;
  const resolvedPageSize =
    pagination?.pageSize ?? pageSize ?? (legacyDataTableCompatibility ? 10 : 25);
  const Root = legacyDataTableCompatibility ? 'div' : 'section';
  // Phase F1 Sprint 1 — apply user column order when supplied. The user
  // can drop columns to the end via the Customise panel; columns missing
  // from `columnOrder` retain their declaration position appended at the
  // end (graceful path when a new column ships after the user customised).
  const orderedColumns: ReadonlyArray<DataGridColumn<T>> =
    columnOrder && columnOrder.length > 0
      ? (() => {
          const byKey = new Map(columns.map((c) => [c.key, c]));
          const seen = new Set<string>();
          const ordered: DataGridColumn<T>[] = [];
          for (const key of columnOrder) {
            const col = byKey.get(key);
            if (col && !seen.has(key)) {
              ordered.push(col);
              seen.add(key);
            }
          }
          for (const col of columns) {
            if (!seen.has(col.key)) {
              ordered.push(col);
              seen.add(col.key);
            }
          }
          return ordered;
        })()
      : columns;

  // Map DataGridColumn → DataTableColumn (drops `align`, `csvValue`,
  // `mobileHidden` — DataTable doesn't know about those yet; Sprint 1
  // v0.2 will lift those down).
  const tableColumns: DataTableColumn<T>[] = orderedColumns.map((c) => ({
    key: c.key,
    header: c.header,
    sortable: c.sortable,
    locked: c.locked,
    render: c.render,
    className: c.className,
    minWidth: c.minWidth,
    technical: c.technical,
  }));

  // Bulk actions pass straight through to DataTable's BulkActionBar.
  // Codex Phase A pass 1: previously the grid rendered its own
  // BulkActionBar in addition to DataTable's → two "N selected" bars.
  // Wrap the consumer's handler so it receives the flat selectedKeys
  // string[] matching the public contract.
  const tableBulkActions: BulkAction[] = selection
    ? selection.bulkActions.map((action) => ({
        label: action.label,
        variant: action.variant,
        onClick: () => action.onClick(Array.from(selectedRows ?? new Set<string>())),
      }))
    : (compatibilityBulkActions ?? []);

  // Phase F1 Sprint 1 — wire the new setter + column-controls slots.
  // The toolbar renders these inline; consumers control the underlying
  // state.
  const filterControlsSlot =
    onFilterChange && filters && filters.length > 0 ? (
      <DataGridFilterControls
        filters={filters}
        activeFilters={activeFilters ?? {}}
        onFilterChange={onFilterChange}
      />
    ) : null;

  // Column controls only render when AT LEAST ONE of the customisation
  // handlers is wired. This avoids surfacing a "Customise" affordance
  // that does nothing (consumer wired the grid but didn't persist prefs).
  const columnControlsSlot =
    onColumnVisibilityChange || onColumnOrderChange || onDensityChange ? (
      <DataGridColumnControls
        inline={compactControls || toolbarMode === 'register'}
        columns={orderedColumns.map((c) => ({ key: c.key, label: c.header, locked: c.locked }))}
        visibility={Object.fromEntries(
          orderedColumns.map((c) => [
            c.key,
            c.locked || (columnVisibility?.[c.key] ?? !c.technical),
          ]),
        )}
        order={columnOrder ?? []}
        density={density}
        onVisibilityChange={onColumnVisibilityChange ?? (() => {})}
        onOrderChange={onColumnOrderChange ?? (() => {})}
        onDensityChange={onDensityChange ?? (() => {})}
        onReset={onResetCustomisation}
        isCustomised={isCustomised}
      />
    ) : null;

  // Phase F1 Sprint 2 — saved-views renders alongside the other action
  // slots. Order in the action row: consumer-supplied actions, savedViews,
  // column-controls, CSV. Phase F4 S8 — only pass combinedActions when at
  // least one slot has content so the empty-toolbar gate downstream can
  // suppress the bordered shell for consumers that don't use the toolbar.
  const combinedActions =
    actions ||
    (!legacyDataTableCompatibility && toolbar) ||
    (!compactControls && savedViewsSlot) ? (
      <>
        {actions}
        {!legacyDataTableCompatibility ? toolbar : null}
        {!compactControls ? savedViewsSlot : null}
      </>
    ) : undefined;

  const toolbarProps: DataGridToolbarProps = {
    compactControls,
    moreControls:
      compactControls && savedViewsSlot ? (
        <>
          {moreControls}
          {savedViewsSlot}
        </>
      ) : (
        moreControls
      ),
    primaryControls,
    title,
    totalCount: data.length,
    countLabel: pagination ? `${data.length.toLocaleString('en-AU')} on this page` : undefined,
    searchValue,
    onSearchChange,
    searchPlaceholder,
    filters,
    activeFilters,
    onFilterClear,
    onClearAllFilters,
    actions: combinedActions,
    onExportCsv: onExportRequest,
    filterControlsSlot,
    columnControlsSlot,
  };

  const shouldShowEmpty =
    !legacyDataTableCompatibility &&
    !loading &&
    !error &&
    data.length === 0 &&
    (!pagination || (pagination.page <= 1 && !pagination.hasMore));
  const shouldShowError = !loading && !!error;
  const resolvedEmptyState: DataGridEmptyState = emptyState ?? {
    title: 'No records found',
    description: emptyMessage ?? 'No records are available for the current filters.',
  };

  return (
    <Root
      aria-label={legacyDataTableCompatibility ? undefined : (title ?? 'List')}
      className={cn(
        legacyDataTableCompatibility
          ? 'w-full min-w-0'
          : 'experience-candidate-data-grid flex min-w-0 flex-col overflow-hidden rounded-card border border-border bg-surface-sheet',
        !legacyDataTableCompatibility && className,
      )}
      data-data-grid
    >
      {!legacyDataTableCompatibility && toolbarMode === 'integrated' ? (
        <DataGridToolbar {...toolbarProps} />
      ) : null}
      {toolbarMode === 'register' ? (
        <DataGridRegisterToolbar
          title={title}
          totalCount={data.length}
          countLabel={
            pagination ? `${data.length.toLocaleString('en-AU')} on this page` : undefined
          }
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchPlaceholder={searchPlaceholder}
          filters={filters}
          activeFilters={activeFilters}
          onFilterChange={onFilterChange}
          onFilterClear={onFilterClear}
          onClearAllFilters={onClearAllFilters}
          columnControlsSlot={columnControlsSlot}
          savedViewsSlot={savedViewsSlot}
          moreControls={moreControls}
          primaryControls={primaryControls}
          actions={actions}
          toolbar={toolbar}
          onExportRequest={onExportRequest}
          registerToolbar={registerToolbar}
        />
      ) : null}

      {loading ? (
        <div className="p-4">
          <LoadingSkeleton lines={Math.min(resolvedPageSize, 8)} />
        </div>
      ) : shouldShowError ? (
        <div className="p-6">
          <ErrorState message={error?.message ?? 'Something went wrong loading this list.'} />
          {onRetry && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex items-center gap-1.5 rounded-control border border-border bg-surface-sheet px-3 py-1.5 text-xs font-medium text-text-heading hover:bg-surface-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible"
              >
                <AlertCircle className="h-3 w-3" aria-hidden="true" />
                Retry
              </button>
            </div>
          )}
        </div>
      ) : shouldShowEmpty ? (
        <div className="p-6">
          <EmptyState
            variant={compactControls ? 'compact' : 'default'}
            icon={resolvedEmptyState.icon as LucideIcon | undefined}
            title={resolvedEmptyState.title}
            description={resolvedEmptyState.description}
            actionLabel={resolvedEmptyState.cta?.label}
            onAction={resolvedEmptyState.cta?.onClick}
          />
        </div>
      ) : (
        <div className="min-w-0" data-density={density}>
          <DataTable<T>
            /*
             * Codex Phase A pass 2 P2: DataTable owns its own
             * `currentPage` state internally and never resets it when
             * `data` changes. So if a user paginates to page 5, then
             * applies a filter that narrows data to 3 rows (fits on
             * page 1), DataTable stays on page 5 → slices past the
             * end → empty row → pagination controls hidden →
             * inaccessible results. Remounting via a key that includes
             * search + filter signature forces a fresh page-1 state on
             * every query change.
             */
            /*
             * F1 S2 pass 2 finding — include pageSize in the remount key
             * so a saved-view that changes ONLY pageSize doesn't leave
             * DataTable's internal currentPage stale (resulting in empty
             * rows + hidden pagination when the new size collapses total
             * pages).
             */
            key={`${searchValue ?? ''}|${JSON.stringify(activeFilters ?? {})}|${data.length}|${resolvedPageSize}`}
            columns={tableColumns}
            data={data as T[]}
            pageSize={resolvedPageSize}
            pagination={pagination}
            density={density}
            className={cn(legacyDataTableCompatibility ? className : 'rounded-none border-0')}
            emptyMessage={emptyMessage}
            toolbar={legacyDataTableCompatibility ? toolbar : undefined}
            stickyHeader={stickyHeader}
            columnVisibility={columnVisibility}
            /*
             * Codex Phase A pass 4 P2: only enable selection when the
             * FULL controlled-state contract is present. A consumer that
             * passed `selection` but forgot `selectedRows` +
             * `onSelectionChange` would otherwise see checkboxes whose
             * toggles never persist + bulk actions that always receive
             * an empty selection. Gate at the source.
             */
            selectable={
              selection
                ? !!selectedRows && !!onSelectionChange
                : legacyDataTableCompatibility
                  ? selectable
                  : !!selectable && !!selectedRows && !!onSelectionChange
            }
            selectedRows={selectedRows}
            onSelectionChange={onSelectionChange}
            rowKey={selection?.rowKey ?? compatibilityRowKey}
            isRowSelectable={isRowSelectable}
            bulkActions={tableBulkActions}
            onRowClick={onRowClick}
            mobileCardRenderer={mobileCardRenderer}
            mobileCardBreakpoint={mobileCardBreakpoint}
            rowClassName={rowClassName}
            rowTestId={rowTestId}
            clientPage={clientPage}
            onClientPageChange={onClientPageChange}
            sortMode={sortMode}
            sort={sort}
            onSortChange={onSortChange}
            pageSizeOptions={pageSizeOptions}
            itemLabel={itemLabel}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </Root>
  );
}
